import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";

const JTD_LOGO = "https://customer-assets.emergentagent.com/job_task-command-center-25/artifacts/cpload9r_JTD.png";
const SWEET_LOGO = "https://customer-assets.emergentagent.com/job_task-command-center-25/artifacts/0sk7wcfv_Sweet%20CHS%20LOGO%20.png";

export default function Login() {
  const { login, error, user } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  React.useEffect(() => {
    if (user && typeof user === "object") navigate("/");
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const result = await login(email.trim().toLowerCase(), password);
    setSubmitting(false);
    if (result) {
      if (result.must_change_password) navigate("/change-password");
      else navigate("/");
    }
  };

  const quickFill = (addr) => {
    setEmail(addr);
    setPassword("changeme123");
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-sand">
      {/* Left split — brand marquee */}
      <div className="relative hidden lg:flex flex-col justify-between p-14 bg-espresso text-sand overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 30%, rgba(255,255,255,0.6), transparent 45%), radial-gradient(circle at 80% 70%, rgba(207,227,223,0.4), transparent 45%)",
          }}
        />
        <div className="relative">
          <div className="text-[11px] uppercase tracking-[0.35em] text-sand/80">Lowcountry Brands</div>
          <div className="mt-3 font-heading text-xl">Workflow Ledger</div>
        </div>

        <div className="relative">
          <h1 className="font-heading text-5xl xl:text-6xl leading-[1.03] max-w-md tracking-tight">
            Craft the day.<br />
            <span className="font-script italic text-seafoam">Track the work.</span>
          </h1>
          <p className="mt-6 max-w-md text-sand/85 leading-relaxed text-base">
            A quiet workflow sheet for Tammy, Jason, Abby, and Jackson — built to keep every job, artwork, and delivery in order across JTD of Charleston and Sweet CHS Designs.
          </p>

          <div className="mt-10 flex items-center gap-6 opacity-95">
            <img src={JTD_LOGO} alt="JTD of CHS" className="h-14 w-auto object-contain brightness-0 invert" />
            <span className="h-10 w-px bg-sand/25" />
            <img src={SWEET_LOGO} alt="Sweet CHS" className="h-10 w-auto object-contain brightness-0 invert" />
          </div>
        </div>

        <div className="relative text-[11px] uppercase tracking-[0.3em] text-sand/60">
          Est. Charleston · Lowcountry
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-8 sm:p-12 paper-grain">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8 flex items-center gap-4">
            <img src={JTD_LOGO} alt="JTD" className="h-10 w-auto object-contain" />
            <span className="h-8 w-px bg-border" />
            <img src={SWEET_LOGO} alt="Sweet CHS" className="h-8 w-auto object-contain" />
          </div>

          <div className="label-caps">Welcome back</div>
          <h2 className="font-heading text-4xl sm:text-5xl mt-3 tracking-tight leading-[1.05]">
            Sign in to the <span className="font-script italic">studio</span>
          </h2>
          <p className="text-muted-foreground mt-4 text-sm leading-relaxed">
            Use the pre-seeded email for your name. Default password:{" "}
            <code className="text-foreground font-medium bg-secondary/60 px-1.5 py-0.5 rounded">changeme123</code>.
          </p>

          <Card className="mt-8 border-border/70 bg-card">
            <CardContent className="p-6 sm:p-7">
              <form onSubmit={submit} className="space-y-5" data-testid="login-form">
                <div>
                  <Label htmlFor="email" className="label-caps">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="tammy@workflow.app"
                    required
                    data-testid="login-email"
                    className="mt-2 bg-background border-border/70 focus-visible:ring-espresso"
                  />
                </div>
                <div>
                  <Label htmlFor="password" className="label-caps">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    data-testid="login-password"
                    className="mt-2 bg-background border-border/70 focus-visible:ring-espresso"
                  />
                </div>

                {error && (
                  <div className="text-sm text-destructive" data-testid="login-error">{error}</div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="login-submit"
                  className="w-full inline-flex items-center justify-center gap-2 border border-espresso bg-espresso text-sand px-6 py-3 text-[11px] uppercase tracking-[0.28em] font-medium hover:bg-espresso/90 transition-colors disabled:opacity-60"
                >
                  {submitting ? "Signing in…" : "Enter Studio"}
                  <span aria-hidden>→</span>
                </button>
              </form>

              <div className="mt-7 pt-6 border-t border-border/60">
                <div className="label-caps mb-3">Quick fill</div>
                <div className="grid grid-cols-2 gap-2">
                  {["tammy", "jason", "abby", "jackson"].map((n) => (
                    <Button
                      key={n}
                      variant="outline"
                      size="sm"
                      onClick={() => quickFill(`${n}@workflow.app`)}
                      data-testid={`quickfill-${n}`}
                      className="justify-start bg-background border-border/70 hover:bg-secondary/60"
                    >
                      <span className="capitalize">{n}</span>
                    </Button>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
